import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DEFAULT_THREAD_ID, DEFAULT_CHAT_PROMPT } from '@/components/AskMeAnything/types';

interface ChatActions {
  setChatTitle: (chatTitle: string) => void;
  setHasChatAnimationFinished: (hasChatAnimationFinished: boolean) => void;
  setIsChatOpen: (isChatOpen: boolean) => void;
  setHasChatStarted: (hasChatStarted: boolean) => void;
  setChatPrompt: (chatPrompt: string) => void;
  clearChatPrompt: () => void;
  setActiveThreadId: (activeThreadId: string) => void;
  clearActiveThreadId: () => void;
  setIsThreadNew: (isThreadNew: boolean) => void;
  setIsThreadsOpen: (isThreadsOpen: boolean) => void;
  setIsQuickActionsOpen: (isQuickActionsOpen: boolean) => void;
  toggleThreadsOpen: () => void;
  resetChat: () => void;
  setIsStreaming: (isStreaming: boolean) => void;
  setStreamAbortController: (streamAbortController: AbortController | null) => void;
}

interface ChatStore {
  chatTitle: string;
  hasChatAnimationFinished: boolean;
  isThreadNew: boolean;
  isThreadsOpen: boolean;
  isQuickActionsOpen: boolean;
  activeThreadId: string;
  hasChatStarted: boolean;
  isChatOpen: boolean;
  chatPrompt: string;
  isStreaming: boolean;
  streamAbortController: AbortController | null;
  actions: ChatActions;
}

const useChatOpenStore = create<ChatStore>()(
  devtools(
    set => ({
      chatTitle: '',
      hasChatAnimationFinished: false,
      activeThreadId: DEFAULT_THREAD_ID,
      chatPrompt: DEFAULT_CHAT_PROMPT,
      isChatOpen: false,
      hasChatStarted: false,
      isThreadNew: true,
      isThreadsOpen: false,
      isQuickActionsOpen: false,
      isStreaming: false,
      streamAbortController: null,
      actions: {
        setChatTitle: (chatTitle: string) => set({ chatTitle }),
        setHasChatAnimationFinished: (hasChatAnimationFinished: boolean) =>
          set({ hasChatAnimationFinished }),
        setIsChatOpen: (isChatOpen: boolean) => set({ isChatOpen }),
        setHasChatStarted: (hasChatStarted: boolean) => set({ hasChatStarted }),
        setChatPrompt: (chatPrompt: string) => set({ chatPrompt }),
        clearChatPrompt: () => set({ chatPrompt: DEFAULT_CHAT_PROMPT }),
        setActiveThreadId: (activeThreadId: string) => set({ activeThreadId }),
        clearActiveThreadId: () => set({ activeThreadId: DEFAULT_THREAD_ID }),
        setIsThreadNew: (isThreadNew: boolean) => set({ isThreadNew }),
        setIsThreadsOpen: (isThreadsOpen: boolean) => set({ isThreadsOpen }),
        setIsQuickActionsOpen: (isQuickActionsOpen: boolean) => set({ isQuickActionsOpen }),
        toggleThreadsOpen: () => set(state => ({ isThreadsOpen: !state.isThreadsOpen })),
        setIsStreaming: (isStreaming: boolean) => set({ isStreaming }),
        setStreamAbortController: (streamAbortController: AbortController | null) =>
          set({ streamAbortController }),
        resetChat: () =>
          set({
            hasChatAnimationFinished: false,
            activeThreadId: DEFAULT_THREAD_ID,
            chatPrompt: DEFAULT_CHAT_PROMPT,
            isChatOpen: false,
            hasChatStarted: false,
            isThreadNew: true,
            isThreadsOpen: false,
            isQuickActionsOpen: false,
            isStreaming: false,
            streamAbortController: null,
            chatTitle: ''
          })
      }
    }),
    {
      name: 'chatStore'
    }
  )
);

export const useIsStreaming = () => useChatOpenStore(state => state.isStreaming);
export const useStreamAbortController = () =>
  useChatOpenStore(state => state.streamAbortController);
export const useIsQuickActionsOpen = () => useChatOpenStore(state => state.isQuickActionsOpen);
export const useIsThreadNew = () => useChatOpenStore(state => state.isThreadNew);
export const useIsThreadsOpen = () => useChatOpenStore(state => state.isThreadsOpen);
export const useActiveThreadId = () => useChatOpenStore(state => state.activeThreadId);
export const useChatPrompt = () => useChatOpenStore(state => state.chatPrompt);
export const useChatTitle = () => useChatOpenStore(state => state.chatTitle);
export const useIsChatOpen = () => useChatOpenStore(state => state.isChatOpen);
export const useHasChatStarted = () => useChatOpenStore(state => state.hasChatStarted);
export const useChatActions = () => useChatOpenStore(state => state.actions);
export const useHasChatAnimationFinished = () =>
  useChatOpenStore(state => state.hasChatAnimationFinished);
